import { FC, useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import useEmblaCarousel from "embla-carousel-react";
import { MdStar } from "react-icons/md";
import { useDirection } from "src/i18n/direction";
import type { PassengerTabsRenderProps } from "../PassengerTabs";
import PassengerStatusBadge from "../components/PassengerStatusBadge";

const fadeClassName =
  "from-background-default pointer-events-none absolute inset-y-0 z-10 w-16 to-transparent transition-opacity duration-300";

const PassengerTabsWeb: FC<PassengerTabsRenderProps> = ({ items, value, onChange, labels }) => {
  const direction = useDirection();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, axis: "x", align: "start", dragThreshold: 6, direction, dragFree: true, containScroll: "trimSnaps" });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const completedCount = items.filter((item) => item.isComplete).length;

  const updateScrollState = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    updateScrollState();
    emblaApi.on("select", updateScrollState).on("reInit", updateScrollState).on("scroll", updateScrollState);
    return () => {
      emblaApi.off("select", updateScrollState).off("reInit", updateScrollState).off("scroll", updateScrollState);
    };
  }, [emblaApi, updateScrollState]);

  useEffect(() => {
    if (!emblaApi) return;
    const index = items.findIndex((item) => item.value === value);
    if (index !== -1 && !emblaApi.slidesInView().includes(index)) emblaApi.scrollTo(index);
  }, [value, emblaApi, items]);

  return (
    <div className="bg-background-default flex flex-col gap-3 rounded-sm p-4 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div className="text-font-default text-base font-semibold">{labels.title}</div>
      </div>

      <div className="relative">
        <span
          className={classNames(
            fadeClassName,
            "start-0 ltr:bg-linear-to-r rtl:bg-linear-to-l",
            canScrollPrev ? "opacity-100" : "opacity-0",
          )}
        />
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex items-stretch gap-2 p-1">
            {items.map((item, index) => {
              const isActive = item.value === value;
              const isNewGroup = index > 0 && items[index - 1].groupKey !== item.groupKey;
              return (
                <div key={item.value} className="flex shrink-0 items-stretch gap-2">
                  {isNewGroup && <span className="bg-border-subtle mx-1 w-px shrink-0" />}
                  <button
                    type="button"
                    onClick={() => onChange(item.value)}
                    className={classNames(
                      "flex min-w-52 shrink-0 cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-start transition-all duration-200",
                      isActive
                        ? "bg-background-default ring-border-active shadow-md ring-2"
                        : item.hasError
                          ? "bg-background-danger/60 ring-font-danger/30 hover:bg-background-danger ring-1"
                          : "bg-background-minimal hover:bg-background-hover",
                    )}
                  >
                    <span className="relative">
                      <PassengerStatusBadge item={item} index={index} isActive={isActive} className="h-9 w-9" />
                      {item.isLead && (
                        <span
                          title={labels.lead}
                          className="bg-background-primary text-font-inverted ring-background-default absolute -end-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full ring-2"
                        >
                          <MdStar className="h-2.5 w-2.5" />
                        </span>
                      )}
                    </span>
                    <span className="flex min-w-0 flex-col leading-tight">
                      <span
                        className={classNames(
                          "max-w-36 truncate text-sm font-semibold",
                          isActive ? "text-font-active" : "text-font-default",
                        )}
                      >
                        {item.name}
                      </span>
                      <span className="text-font-subtle mt-0.5 text-xs">
                        {[item.typeLabel, item.groupLabel].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        <span
          className={classNames(
            fadeClassName,
            "end-0 ltr:bg-linear-to-l rtl:bg-linear-to-r",
            canScrollNext ? "opacity-100" : "opacity-0",
          )}
        />
      </div>
    </div>
  );
};

export default PassengerTabsWeb;
